import React, { useRef, useEffect, useImperativeHandle, useState } from "react";
import Hls, { Events, ErrorData } from "hls.js";
import { CircularProgress } from "@material-ui/core";

type AudioHlsPlayerProps = {
  src: string;
  config?: Partial<ConstructorParameters<typeof Hls>[0]>;

  // HTMLVideoElement props
  autoPlay?: boolean;
  controls?: boolean;
  style?: React.CSSProperties;
  width?: string | number;
  height?: string | number;

  // Eventos
  onPlay?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
  onPause?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
  onSeeked?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
  onTimeUpdate?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
  onError?: (event: Events.ERROR, data: ErrorData) => void;

  // Ref externo
  playerRef?: React.Ref<HTMLVideoElement>;
  onLoadingChange?: (loading: boolean) => void;
};

const AudioHlsPlayer: React.FC<AudioHlsPlayerProps> = ({
  src,
  config,
  autoPlay = false,
  controls = true,
  style,
  width = "100%",
  height = "auto",
  onPlay,
  onPause,
  onSeeked,
  onTimeUpdate,
  onError,
  playerRef,
  onLoadingChange,
}) => {
  const internalRef = useRef<HTMLVideoElement>(null);
  const [loading, setLoading] = useState(true);

  // Expõe o ref interno para fora se playerRef for passado
  useImperativeHandle(playerRef, () => internalRef.current as HTMLVideoElement);

  useEffect(() => {
    if (!internalRef.current || !src) return;

    let hls: Hls | null = null;
    setLoading(true);
    onLoadingChange?.(true);

    if (Hls.isSupported()) {
      hls = new Hls(config);
      hls.loadSource(src);
      hls.attachMedia(internalRef.current);

      if (autoPlay) {
        hls.on(Events.MANIFEST_PARSED, () => {
          setLoading(false);
          onLoadingChange?.(false);

          internalRef.current?.play().catch(() => {
            /* autoplay pode falhar sem interação do usuário */
          });
        });
      }

      hls.on(Events.ERROR, (event, data) => {
        if (onError) {
          onError(event, data);
        }
      });
    } else if (
      internalRef.current.canPlayType("application/vnd.apple.mpegurl")
    ) {
      internalRef.current.src = src;
      internalRef.current.addEventListener("loadedmetadata", () => {
        setLoading(false);
        onLoadingChange?.(false);

        if (autoPlay) {
          internalRef.current?.play();
        }
      });
    }

    return () => {
      hls?.destroy();
    };
  }, [src, config, autoPlay, onError, onLoadingChange]);

  return (
     <div style={{ position: "relative", width, height }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.3)",
            zIndex: 10,
          }}
        >
          <CircularProgress disableShrink />
        </div>
      )}
      <video
        ref={internalRef}
        style={{ width: "100%", height: "100%", ...style }}
        controls={controls}
        autoPlay={autoPlay}
        onPlay={onPlay}
        onPause={onPause}
        onSeeked={onSeeked}
        onTimeUpdate={onTimeUpdate}
      />
    </div>
  );
};

export default AudioHlsPlayer;
